import * as React from "react"

import { Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useFinanceFeatureQuery } from "@/features/finance/services/finance-query"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import type { MoneyText } from "@/features/finance/model/finance-types"
import { FinanceCategoryScope } from "@/features/finance/model/finance-types"
import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { useFinanceOverviewScope } from "@/features/finance/overview/context/finance-overview-context"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import {
  compareWatchlistAmountsDesc,
  formatWatchlistAmount,
  isZeroWatchlistAmount,
} from "@/features/finance/overview/lib/watchlist-display"
import {
  useWidgetCreateActions,
  type WidgetCreateKey,
} from "@/features/finance/overview/lib/use-widget-create-actions"
import {
  ConfigCheckList,
  ConfigSwitchRow,
  WidgetConfigDrawer,
  useWidgetConfig,
} from "@/features/finance/overview/components/widget-config-drawer"

const WIDGET_CREATE_KEYS: readonly WidgetCreateKey[] = ["income", "expense", "transfer"]

/** How many accounts to show by default (before the user picks a specific set). */
const DEFAULT_ROWS = 6

interface AccountWatchlistConfig {
  /** null => show the top accounts by balance; an array => show exactly these accounts. */
  selectedIds: string[] | null
  hideZero: boolean
}

export interface AccountRow {
  id: string
  name: string
  /** Money: the canonical decimal string from the API. Compared with `compareMoney`, never with `a - b`. */
  balance: MoneyText
  currency: string
}

/**
 * Every finance account of the overview's scope with its current balance (period-independent), largest first.
 * Shared by the watchlist widget and the phone's finance overview.
 */
export function useFinanceAccountBalances() {
  const scope = useFinanceOverviewScope()
  const { useFinanceAccountsQuery } = useFinanceFeatureQuery()
  const query = useFinanceAccountsQuery(
    scope.workspaceId
      ? {
          workspaceId: scope.workspaceId,
          branchId: scope.branchId,
          // The system (parish) branch owns its accounts under PARISH scope; a BRANCH scope targeting it is
          // rejected by the backend. Mirror the transaction form's context→scope mapping.
          scope: scope.context === "parish" ? FinanceCategoryScope.Parish : FinanceCategoryScope.Branch,
          includeBalances: true,
          limit: 100,
        }
      : null,
  )
  const accounts = React.useMemo<AccountRow[]>(
    () =>
      (query.data?.items ?? [])
        .map((account) => ({
          id: account.id,
          name: account.name,
          // `balance` is absent unless the list was queried with includeBalances; "0" is money in any currency.
          balance: account.balance ?? "0",
          currency: account.currency ?? scope.currency,
        }))
        // Exact ordering on minor units — see FundWatchlistWidget: `b.balance - a.balance` sorts floats.
        .sort(compareWatchlistAmountsDesc),
    [query.data, scope.currency],
  )
  return { query, accounts }
}

/**
 * Account Watchlist — monitor selected accounts without opening the full Chart of Accounts. Shows each
 * account's current balance (period-independent — kept out of the query key). Per-account period movement
 * and reconciliation status are deferred pending a backend per-account period endpoint.
 */
export function AccountWatchlistWidget() {
  const t = useTranslations("finance.overview")
  const go = useOverviewNav()
  const cfg = useWidgetConfig<AccountWatchlistConfig>({ selectedIds: null, hideZero: false })
  const { query, accounts } = useFinanceAccountBalances()

  const rows = React.useMemo<AccountRow[]>(() => {
    const selected = cfg.config.selectedIds
    const visible = accounts.filter(
      (account) => !cfg.config.hideZero || !isZeroWatchlistAmount(account.balance, account.currency),
    )
    return selected === null ? visible.slice(0, DEFAULT_ROWS) : visible.filter((account) => selected.includes(account.id))
  }, [accounts, cfg.config.hideZero, cfg.config.selectedIds])

  const create = useWidgetCreateActions(WIDGET_CREATE_KEYS)
  useWidgetMenu({
    configure: cfg.openDrawer,
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.accounts) ? () => go(OverviewRoutes.accounts) : undefined,
    create,
  })

  return (
    <>
      <WidgetBoundary
        isLoading={query.isLoading}
        isError={query.isError}
        isFetching={query.isFetching}
        data={query.data ? rows : undefined}
        isEmpty={(r) => r.length === 0}
        skeleton={<RowsSkeleton />}
        errorTitle={t("widgets.accountWatchlist.error")}
        onRetry={() => void query.refetch()}
        retryLabel={t("widgets.common.retry")}
        emptyTitle={t("widgets.accountWatchlist.empty.title")}
      >
        {(accountRows) => (
          <ul className="divide-y divide-border/60">
            {accountRows.map((account) => (
              <li key={account.id}>
                <button
                  type="button"
                  onClick={go.canNavigateTo(OverviewRoutes.accounts) ? () => go(`${OverviewRoutes.accounts}/${account.id}`) : undefined}
                  disabled={!go.canNavigateTo(OverviewRoutes.accounts)}
                  className="group flex w-full items-center justify-between gap-3 py-1.5 text-left"
                >
                  <span className="min-w-0 flex-1 truncate text-sm">{account.name}</span>
                  <span className="shrink-0 text-sm font-medium tabular-nums">
                    {formatWatchlistAmount(account.balance, account.currency)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </WidgetBoundary>

      <WidgetConfigDrawer
        open={cfg.open}
        title={t("widgets.accountWatchlist.title")}
        onSave={cfg.save}
        onCancel={cfg.cancel}
      >
        <ConfigCheckList
          label={t("widgets.accountWatchlist.config.accounts")}
          emptyLabel={t("widgets.accountWatchlist.empty.title")}
          items={(query.data?.items ?? []).map((account) => ({ id: account.id, label: account.name }))}
          selectedIds={cfg.draft.selectedIds}
          onChange={(ids) => cfg.patch({ selectedIds: ids })}
        />
        <ConfigSwitchRow
          label={t("widgets.accountWatchlist.config.hideZero")}
          checked={cfg.draft.hideZero}
          onCheckedChange={(value) => cfg.patch({ hideZero: value })}
        />
      </WidgetConfigDrawer>
    </>
  )
}

function RowsSkeleton() {
  return (
    <div className="space-y-2.5 py-1">
      {[64, 52, 72, 48, 60].map((w, index) => (
        <div key={index} className="flex items-center justify-between gap-3">
          <Skeleton className="h-3.5" style={{ width: `${w}%` }} />
          <Skeleton className="h-3.5 w-16" />
        </div>
      ))}
    </div>
  )
}
